'use client';

import { useTranslation } from 'next-i18next';
import { usePathname, useRouter } from 'next/navigation';
import { useSidebarNavigationOptional } from '@components/contexts/SidebarNavigationContext';
import { useMcSharedStateOptional } from '@components/contexts/McSharedStateContext';
import type { PromptBarMode } from './usePromptBarMode';
import { useCurrentMission } from './useCurrentMission';

type MissionContext = 'candidate' | 'company';

export interface ActionItem {
  key: string;
  label: string;
  /** Which context this mission belongs to — used only to group the items in the menu. */
  context: MissionContext;
  /** Destination URL, used when no inAppAction is provided. */
  href: string;
  /** Optional soft-navigation fast path (no full reload). */
  inAppAction?: () => void;
  selected: boolean;
}

interface PromptBarActions {
  candidateItems: ActionItem[];
  companyItems: ActionItem[];
  handleClick: (item: ActionItem) => void;
}

const loginRedirect = (target: string) => `/login?_target_path=${encodeURIComponent(target)}`;

/**
 * Builds the candidate/company mission items and the click handler shared by every
 * prompt-bar navigation surface (the `+` popup and the mission chips).
 */
export const usePromptBarActions = (
  locale: Locale,
  mode: PromptBarMode,
  isAuthenticated: boolean,
): PromptBarActions => {
  const { t } = useTranslation('common');
  const pathname = usePathname();
  const router = useRouter();
  const sidebarNav = useSidebarNavigationOptional();
  const mcSharedState = useMcSharedStateOptional();
  const mission = useCurrentMission(locale);

  const mcPath = `/${locale}/mc`;
  const bestieChatPath = `/${locale}/mc/bestie-chat`;
  const myJobsPath = `/${locale}/mc/my-jobs`;
  const addJobPath = `/${locale}/mc/job-add/edit`;
  const editProfilePath = `/${locale}/profile`;

  const isSelected = (key: string): boolean => mission.key === key;

  const candidateItems: ActionItem[] = [
    {
      key: 'jobs',
      label: t('mc.chip.jobs'),
      context: 'candidate',
      href: mcPath,
      // `/mc` is the job-search home in any context, so the soft-nav fast path works for both.
      inAppAction: sidebarNav ? () => sidebarNav.navigateToNewJobSearchForm() : undefined,
      selected: isSelected('jobs'),
    },
    {
      key: 'edit-profile',
      label: t('mc.chip.profile'),
      context: 'candidate',
      href: isAuthenticated ? editProfilePath : loginRedirect(editProfilePath),
      selected: isSelected('edit-profile'),
    },
    {
      key: 'coaching',
      label: t('mc.chip.coach'),
      context: 'candidate',
      href: bestieChatPath,
      // Open the chat input ready to type so the user doesn't have to tap it after
      // landing on the coaching view (ChatBranch reads this on mount).
      inAppAction: sidebarNav
        ? () => { mcSharedState?.requestChatFocus(); sidebarNav.navigateToNewSearch(); }
        : undefined,
      selected: isSelected('coaching'),
    },
  ];

  const companyItems: ActionItem[] = [
    {
      key: 'add-job',
      label: t('mc.chip.add'),
      context: 'company',
      href: isAuthenticated ? addJobPath : loginRedirect(addJobPath),
      selected: isSelected('add-job'),
    },
    {
      key: 'candidate-management',
      label: t('mc.chip.admin'),
      context: 'company',
      href: isAuthenticated ? myJobsPath : loginRedirect(myJobsPath),
      selected: isSelected('candidate-management'),
    },
  ];

  const isOnSamePage = (href: string): boolean => {
    if (!pathname) return false;
    const localePrefix = `/${locale}`;
    const stripLocale = (p: string): string => {
      if (p === localePrefix) return '/';
      if (p.startsWith(`${localePrefix}/`)) return p.slice(localePrefix.length);
      return p;
    };
    const normalize = (p: string): string => {
      const stripped = stripLocale(p.split('?')[0]);
      return stripped === '/' ? stripped : stripped.replace(/\/$/, '');
    };
    return normalize(href) === normalize(pathname);
  };

  const handleClick = (item: ActionItem) => {
    if (item.selected) return;
    if (typeof window === 'undefined') return;

    // Mission selection never switches context — only the explicit sidebar switch does. A
    // cross-context mission just navigates; its page renders inside whichever context shell
    // is currently mounted (pages are shell-agnostic). Nothing to do when already there.
    if (isOnSamePage(item.href)) return;

    if (item.inAppAction) {
      item.inAppAction();
      return;
    }
    // Soft-navigate within the App Router (keeps the persistent MC shell mounted). For
    // Pages-Router targets (e.g. the /login redirect) Next falls back to a hard navigation.
    router.push(item.href);
  };

  return { candidateItems, companyItems, handleClick };
};
